import {createContext, useState, useEffect, useContext, ReactNode} from "react";
import axios from "axios";
import {User} from "@/types/User.ts";

export interface AuthContextType {
    loggedInUser: User | null;
    setLoggedInUser: (userInfo: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [loggedInUser, setLoggedInUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await axios.get('/auth/loggedInUser');
                setLoggedInUser(response.data);
            } catch (error) {
                console.error('Error checking authentication status:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchUser();
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen p-5 min-w-screen">
                <div className="flex space-x-2 animate-pulse">
                    <div className="w-3 h-3 bg-zinc-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-zinc-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-zinc-500 rounded-full"></div>
                </div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={{ loggedInUser, setLoggedInUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
